// ============================================================
// MobileNumberCard.tsx — B4c (D4): the CLIENT's UK mobile for e-signature
// codes (Client.mobileNumber, E.164). A card in the Client details grid (the
// client page and the case's Client details — both show the CLIENT RECORD's
// number; the case keeps no copy), in the same anatomy as its neighbours:
// read view + pencil → the edit form with Cancel / Save.
//
// Unlike the other client fields this one is NOT autosaved: a signing code is
// texted here, so a change is deliberate — "Nothing saves until you press
// Save" — and each real change is audited server-side (masked + hashed,
// MobileNumberChange; "Last changed by … on …" under the number).
// Saves with PATCH /api/clients/:id { mobileNumber } (the server normalises:
// 400 + the plain-English message under the field). On the client page it
// first flushes the client autosave and sends that page's concurrency base.
// The case page passes no value (its copy of the client doesn't carry the
// number): the card reads the client record itself (GET /api/clients/:id).
// Hidden entirely while the firm's e-signature pilot is off.
//
// (E-sign fixes B) The agreed design (utils/esignMobile codesMobileView):
//   • MAIN — the main phone (Client.mainContactNumber) is a UK mobile and no
//     different mobile is stored: the card shows the main phone labelled
//     "(used for e-signature codes)" and no separate field. The pencil can
//     still add a DIFFERENT mobile (blank keeps the main phone). Save sends
//     mobileNumber: null — the server uses the main number.
//   • SEPARATE — a different mobile is stored, or the main phone isn't a UK
//     mobile: "Mobile for e-signature codes", with "This looks like a landline
//     — codes need a mobile" when the main phone is a landline and no mobile
//     is recorded. Save sends the typed mobile (one equal to the main phone
//     is sent as null — the same thing, MAIN).
// The case page and the Clients page pass the same props (mobileCardPropsOf),
// so both always show the same thing.
// ============================================================
import React, { useEffect, useId, useState } from 'react'
import { getClientByIdApi, getClientMobileChangesApi, updateClientApi } from '../../api/casevoApi'
import { useEsignPilot } from '../../hooks/useEsignPilot'
import { useCasevoStore } from '../../store/useCasevoStore'
import { flushClientAutoSave } from '../../hooks/useClientAutoSave'
import { getClientBaseUpdatedAt, setClientBaseUpdatedAt } from '../../hooks/saveBaseVersion'
import { MOBILE_FORMAT_ERROR, formatUkMobile } from '../../utils/mobileNumber'
import { CODES_LABEL_SUFFIX, CODES_MOBILE_LABEL, LANDLINE_NOTE, codesMobileView, normaliseUkMobile } from '../../utils/esignMobile'
import { errorText } from '../billing/billingUi'
import { ErrorNote } from '../shared/ActionBox'
import type { Client } from '../../types/client.types'
import { CARD, CARD_EDITING, BTN_SEC, BTN_PRI, SM } from './ClientInfoSections'
import { IcEdit, IcCheck, IcPhone } from './clientInfoRegistry'
import { MOBILE_HINT, MobileChangedHint, MobileInput, MobileValue, mobileE164Of, mobileInputError } from './MobileNumberField'

// A value saved from here while the page's copy (the prop) is still the old
// one — e.g. the case page, whose client copy isn't re-read on save. Shown
// until the prop moves on (then the prop is the truth again).
const savedHere = new Map<string, { base: string | null; saved: string | null }>()
const shownValue = (clientId: string, prop: string | null): string | null => {
  const c = savedHere.get(clientId)
  return c && c.base === prop ? c.saved : prop
}
export const _resetMobileCardCache = (): void => savedHere.clear()

// (E-sign fixes B) The card's props from a client record — the case page
// (currentCase.client) and the Clients page (selectedClient) both use this.
export const mobileCardPropsOf = (c: Pick<Client, 'id' | 'mainContactNumber'> & { mobileNumber?: string | null }): { clientId: string; value: string | null | undefined; mainPhone: string | null } => ({
  clientId: c.id,
  // Absent (an older case payload) → the card reads the record itself.
  value: c.mobileNumber === undefined ? undefined : c.mobileNumber,
  mainPhone: c.mainContactNumber ?? null,
})

export const MAIN_PHONE_CODES_LABEL = `Main phone ${CODES_LABEL_SUFFIX}`
const BIG = 'text-[17px] font-semibold tracking-[-0.005em]'
const LINK = 'text-casevo-forest no-underline border-b border-casevo-border-soft hover:text-casevo-brand hover:border-casevo-mid focus:outline-none casevo-focus rounded-[2px]'
const DT = 'text-[11px] font-bold tracking-[0.1em] uppercase text-casevo-text-soft mb-[3px]'

const MobileNumberCard: React.FC<{
  clientId: string
  // The client record's number (E.164) as this page has it; undefined = not
  // known here → the card loads the client record.
  value?: string | null
  // (E-sign fixes B) The client record's main phone (mainContactNumber);
  // undefined → taken from the record the card loads.
  mainPhone?: string | null
  // After a save — e.g. the client page updates its working copy.
  onSaved?: (client: Client) => void
  className?: string
}> = ({ clientId, value, mainPhone, onSaved, className = '' }) => {
  const pilot = useEsignPilot()
  const id = useId()
  const [loaded, setLoaded] = useState<{ value: string | null; phone: string | null } | null>(null)
  const [loadErr, setLoadErr] = useState<string | null>(null)
  const selfLoad = value === undefined
  const prop = selfLoad ? loaded?.value ?? null : value
  const phone = mainPhone !== undefined ? mainPhone : loaded?.phone ?? null
  const [saved, setSaved] = useState<string | null>(() => shownValue(clientId, prop))
  const [draft, setDraft] = useState<string | null>(null)
  // The mode the form was opened in (MAIN = adding a different mobile).
  const [openedAs, setOpenedAs] = useState<'MAIN' | 'SEPARATE'>('SEPARATE')
  const [error, setError] = useState<string | undefined>(undefined)
  const [saveErr, setSaveErr] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [rev, setRev] = useState(0)
  const editing = draft !== null

  useEffect(() => { if (!editing) setSaved(shownValue(clientId, prop)) }, [clientId, prop, editing])
  useEffect(() => {
    if (!pilot || !selfLoad) return undefined
    let live = true
    setLoadErr(null)
    getClientByIdApi(clientId)
      .then((c) => { if (live) setLoaded({ value: c.mobileNumber ?? null, phone: c.mainContactNumber ?? null }) })
      .catch((e) => { if (live) setLoadErr(errorText(e, 'Couldn’t load the mobile number.')) })
    return () => { live = false }
  }, [clientId, pilot, selfLoad])

  if (!pilot) return null

  const view = codesMobileView({ phone, mobile: saved })
  const mainE164 = normaliseUkMobile(phone)
  const open = (): void => {
    setOpenedAs(view.mode)
    setDraft(view.mode === 'MAIN' ? '' : formatUkMobile(saved)); setError(undefined); setSaveErr(null)
  }
  const cancel = (): void => { setDraft(null); setError(undefined); setSaveErr(null) }
  const save = async (): Promise<void> => {
    if (draft === null || busy) return
    const bad = mobileInputError(draft)
    if (bad) { setError(bad); return }
    const typed = mobileE164Of(draft)
    // MAIN (blank with a mobile main phone, or the same number) → null: the server uses the main number.
    const toMain = !!mainE164 && (typed === null || typed === mainE164)
    const next = toMain ? null : typed
    if (next === (saved ? normaliseUkMobile(saved) ?? saved : null)) { cancel(); return }
    setBusy(true); setSaveErr(null)
    try {
      // The client page: its autosave first, then this save on the same
      // concurrency base (someone else's change since → 409, said below).
      const onClientPage = useCasevoStore.getState().selectedClient?.id === clientId
      if (onClientPage) await flushClientAutoSave()
      const base = onClientPage ? getClientBaseUpdatedAt(clientId) : null
      const updated = await updateClientApi(clientId, { mobileNumber: next === null ? null : draft.trim(), ...(base ? { baseUpdatedAt: base } : {}) })
      const stored = updated.mobileNumber ?? null
      if (onClientPage) setClientBaseUpdatedAt(clientId, updated.updatedAt, 'mobile-save')
      savedHere.set(clientId, { base: prop, saved: stored })
      if (selfLoad) setLoaded({ value: stored, phone: updated.mainContactNumber ?? phone })
      setSaved(stored); setDraft(null); setRev((n) => n + 1)
      onSaved?.(updated)
    } catch (e) {
      const resp = (e as { response?: { status?: number; data?: { field?: string; error?: string } } }).response
      if (resp?.status === 400 && resp.data?.field === 'mobileNumber') setError(resp.data.error ?? MOBILE_FORMAT_ERROR)
      else if (resp?.status === 409) setSaveErr('Someone else changed this client just now. Reload the page to see their changes, then try again.')
      else setSaveErr(errorText(e, 'Couldn’t save the mobile number. Please try again.'))
    } finally { setBusy(false) }
  }

  const mainShown = phone?.trim() ? (mainE164 ? formatUkMobile(mainE164) : phone.trim()) : ''
  const hint = openedAs === 'MAIN'
    ? `Leave blank to keep using the main phone, ${mainShown}.`
    : <>{MOBILE_HINT}{mainE164 ? ` Leave blank to use the main phone, ${mainShown}.` : ''}</>
  const landline = view.landlineNote && (
    <p className="m-0 mt-1 text-[12px] text-casevo-warn" data-testid="landline-note">
      Main phone {mainShown}. {LANDLINE_NOTE}.
    </p>
  )

  return (
    <section aria-label="Mobile for e-signature" data-codes-mode={view.mode} className={`${CARD} group/card overflow-hidden flex flex-col transition-[border-color,box-shadow] ${editing ? CARD_EDITING : ''} ${className}`}>
      <div className="flex items-center gap-[11px] pt-[14px] pr-4 pb-3 pl-[18px]">
        <span className="w-8 h-8 rounded-[9px] bg-casevo-mint text-casevo-brand dark:text-casevo-light grid place-items-center shrink-0">{IcPhone(17)}</span>
        <div className="min-w-0">
          <h3 className="m-0 text-[15px] font-semibold tracking-[-0.005em] leading-[1.2] text-casevo-forest">E-signature</h3>
          <div className="text-[12px] text-casevo-text-soft mt-px">Where signing codes are texted</div>
        </div>
        <div className="ml-auto flex gap-1 items-center">
          {editing ? <span className="text-[12.5px] text-casevo-text-soft">Editing</span> : selfLoad && !loaded ? null : (
            <button type="button" onClick={open} title="Edit mobile for e-signature" aria-label="Edit mobile for e-signature"
              className="w-[30px] h-[30px] rounded-[8px] grid place-items-center text-casevo-text-soft hover:bg-casevo-mint hover:text-casevo-brand transition-[opacity,background-color,color] focus:outline-none casevo-focus md:opacity-0 md:group-hover/card:opacity-100 md:group-focus-within/card:opacity-100">
              {IcEdit(15)}
            </button>
          )}
        </div>
      </div>
      <div className="pt-[2px] px-[18px] pb-4 flex-1">
        {selfLoad && !loaded && !editing ? (
          loadErr ? <ErrorNote>{loadErr}</ErrorNote> : <p role="status" className="m-0 text-[13px] text-casevo-text-soft">Loading…</p>
        ) : editing ? (
          <div className="grid grid-cols-1 gap-y-3">
            {openedAs === 'MAIN' && (
              <p className="m-0 text-[13px] text-casevo-forest">Codes go to the main phone, <span className="tabular-nums font-semibold">{mainShown}</span>. Add a different mobile only if the client wants codes sent somewhere else.</p>
            )}
            <MobileInput id={`${id}-mobile`} value={draft ?? ''} onChange={(v) => { setDraft(v); setError(undefined); setSaveErr(null) }} error={error} disabled={busy}
              label={CODES_MOBILE_LABEL} hint={hint} placeholder={openedAs === 'MAIN' ? 'A different mobile, e.g. 07911 123456' : undefined} />
            {openedAs === 'SEPARATE' && landline}
            {saveErr && <ErrorNote>{saveErr}</ErrorNote>}
          </div>
        ) : (
          <dl className="grid grid-cols-1 gap-y-3 m-0">
            <div className="min-w-0">
              <dt className={DT}>{view.mode === 'MAIN' ? MAIN_PHONE_CODES_LABEL : CODES_MOBILE_LABEL}</dt>
              <dd className="m-0 text-[14.5px] text-casevo-forest leading-[1.45] flex items-start gap-1.5" data-testid="client-mobile-value">
                <MobileValue e164={view.number} className={`${LINK} ${BIG}`}
                  empty={<span className="text-casevo-text-soft italic text-[13.5px]">Not recorded<button type="button" onClick={open} className="not-italic ml-1.5 text-casevo-brand dark:text-casevo-mid font-semibold text-[12.5px] focus:outline-none casevo-focus rounded-[3px]">Add</button></span>} />
              </dd>
              {landline}
              <MobileChangedHint load={() => getClientMobileChangesApi(clientId)} refreshKey={`${clientId}:${rev}`} />
            </div>
          </dl>
        )}
      </div>
      {editing && (
        <div className="flex items-center gap-2 justify-end flex-wrap py-[10px] px-[14px] bg-casevo-input border-t border-casevo-border-soft">
          <span className="mr-auto text-[12px] text-casevo-text-soft">Nothing saves until you press Save · Cancel discards</span>
          <button type="button" className={`${BTN_SEC} ${SM}`} onClick={cancel} disabled={busy}>Cancel</button>
          <button type="button" className={`${BTN_PRI} ${SM}`} onClick={() => { void save() }} disabled={busy}>{IcCheck(13)}{busy ? 'Saving…' : 'Save'}</button>
        </div>
      )}
    </section>
  )
}

export default MobileNumberCard
